import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { compact, isEmpty, isEqual, map } from "lodash/fp";
import { DateTime } from "luxon";

import {
  getParagraphId,
  getTurnEndTimeFromWords,
  getTurnStartTimeFromWords,
} from "@app/analytics/api/models";
import EditableContent, {
  EditableContentProps,
} from "@app/analytics/MeetingReport/components/EditableContent";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import type {
  TranscriptEdit,
  TranscriptItem,
  TranscriptSearchResult,
} from "@app/analytics/MeetingReport/types";
import {
  chunkContentBySearchResults,
  isTurnParagraph,
} from "@app/analytics/MeetingReport/utils";
import {
  getEditedWords,
  getTemporaryId,
  isNewParagraph,
  splitWords,
} from "@app/analytics/MeetingReport/utils/editUtils";
import type {
  TranscriptTurnParagraph,
  TranscriptWord,
} from "@app/common/types";
import {
  getFormattedTimestampFromMs,
  msDurationBetween,
} from "@app/common/utils";
import { parseUTCDateTime } from "@app/utils/utils";

const shouldSkipDeleteConfirmation = (paragraph: TranscriptTurnParagraph) => {
  return isNewParagraph(paragraph) && !paragraph.text;
};

const createNewTurnFromParagraph = (
  paragraph: TranscriptTurnParagraph,
  {
    asNewParagraph,
    words,
  }: { asNewParagraph: boolean; words: TranscriptWord[] }
): TranscriptTurnParagraph => {
  const rawStartTimeFromWords = getTurnStartTimeFromWords(words);
  const rawEndTimeFromWords = getTurnEndTimeFromWords(words);

  const rawStartTime = rawStartTimeFromWords
    ? parseUTCDateTime(rawStartTimeFromWords)
    : asNewParagraph
      ? paragraph.rawEndTime
      : paragraph.turn.rawEndTime;

  const rawEndTime = rawEndTimeFromWords
    ? parseUTCDateTime(rawEndTimeFromWords)
    : asNewParagraph
      ? paragraph.rawEndTime
      : paragraph.turn.rawEndTime;

  // ensure the new turn/paragraph's start time is the same or later than the
  // current paragraph's start time
  const resolvedStartTime = DateTime.max(rawStartTime, paragraph.startTime);

  // ensure the new turn/paragraph's end time is the same or earlier than the
  // current paragraph's end time
  const resolvedEndTime = DateTime.min(rawEndTime, paragraph.endTime);

  // the new turn's relative start time is original turn's start time ms
  // + duration between turn's start time and new turn/paragraph's start time
  const newTurnStartTimeMs =
    paragraph.turn.relativeStartTime.raw +
    msDurationBetween(paragraph.turn.rawStartTime, resolvedStartTime);

  const turnId = asNewParagraph ? paragraph.turn.id : getTemporaryId();

  const newTurnParagraph: TranscriptTurnParagraph = {
    id: getParagraphId(turnId),
    isDeleted: false,
    startTime: resolvedStartTime,
    endTime: resolvedEndTime,
    rawStartTime,
    rawEndTime,
    previousParagraphId: asNewParagraph ? paragraph.id : undefined,
    text: map("value", words).join(" "),
    words,
    turn: asNewParagraph
      ? paragraph.turn
      : {
          id: turnId,
          isEdited: true,
          paragraphs: [],
          previousTurnId: paragraph.turn.id,
          startTime: resolvedStartTime,
          endTime: resolvedEndTime,
          rawStartTime,
          rawEndTime,
          speaker: paragraph.turn.speaker,
          relativeStartTime: {
            raw: newTurnStartTimeMs,
            formatted: getFormattedTimestampFromMs(newTurnStartTimeMs),
          },
        },
  };

  return newTurnParagraph;
};

interface EditableTranscriptTurnItemContentProps {
  paragraph: TranscriptTurnParagraph;
  nextItem?: TranscriptItem;
}

const EMPTY_SEARCH_RESULTS: TranscriptSearchResult[] = [];

const EditableTranscriptTurnItemContent = ({
  paragraph,
  nextItem,
}: EditableTranscriptTurnItemContentProps) => {
  const searchResults = useSearchStore(
    (state) =>
      state.searchResultsByParagraphId.get(paragraph.id) ?? EMPTY_SEARCH_RESULTS
  );

  const chunkedContent = useMemo(() => {
    if (searchResults.length) {
      return chunkContentBySearchResults(paragraph.words, searchResults);
    }
    return paragraph.text;
  }, [searchResults, paragraph.text]);

  const editedWordsRef = useRef<TranscriptWord[]>(paragraph.words);

  const syncEditedWordsRef = useCallback(() => {
    if (isEqual(editedWordsRef.current, paragraph.words)) return;
    editedWordsRef.current = paragraph.words;
  }, [paragraph]);

  const {
    addParagraph,
    addTurn,
    deleteTurn,
    isEditingParagraph,
    onCloseEdit,
    onStartEdit,
    saveEdit,
    setModal,
    setTurnToDelete,
  } = useEditStore((state) => ({
    addParagraph: state.addParagraph,
    addTurn: state.addTurn,
    deleteTurn: state.deleteTurn,
    isEditingParagraph: state.transcript.activeParagraph?.id === paragraph.id,
    onCloseEdit: state.exitItem,
    onStartEdit: state.startEditingItem,
    saveEdit: state.save,
    setModal: state.setModal,
    setTurnToDelete: state.setTurnToDelete,
  }));

  useEffect(() => {
    // if the value coming into the component changes, update the ref
    if (isEditingParagraph) {
      syncEditedWordsRef();
    }
  }, [isEditingParagraph]);

  const handleInput = (updatedText: string) => {
    const editedWords = getEditedWords(editedWordsRef.current, updatedText);
    editedWordsRef.current = editedWords;
  };

  const handleToggleEditing = (editing: boolean) => {
    if (editing) {
      onStartEdit({ type: "turn", item: paragraph });
    } else {
      onCloseEdit();
    }
  };

  const handleDeleteTurn = (force = false) => {
    if (force || shouldSkipDeleteConfirmation(paragraph)) {
      deleteTurn(paragraph);
      return;
    }
    // deletion is handled in DeleteTurnConfirmationModal
    setTurnToDelete(paragraph);
    setModal("confirm_delete_turn");
  };

  const handleDiscard = () => {
    if (!paragraph.text) {
      // if we're discarding a turn that has no text, just delete it
      handleDeleteTurn(true);
    }
  };

  const getTextUpdateEdit = ({
    text,
    words,
  }: {
    text: string;
    words?: TranscriptWord[];
  }) => {
    const edit: TranscriptEdit = {
      type: "turn",
      before: paragraph,
      after: {
        ...paragraph,
        words: words ?? editedWordsRef.current,
        text,
      },
    };
    return edit;
  };

  const handleSaveEdits = (updatedText: string) => {
    // treat a turn with empty updated text as a delete
    if (isEmpty(updatedText)) {
      handleDeleteTurn();
      return;
    }

    if (updatedText === paragraph.text) return;

    const updateEdit = getTextUpdateEdit({ text: updatedText });
    saveEdit(updateEdit);
  };

  const handleEnter: EditableContentProps["onEnter"] = (
    event,
    { preValue, postValue }
  ) => {
    // save any changes that were made to the existing turn, if text was split
    const updateCurrentEdit =
      preValue === paragraph.text
        ? null
        : getTextUpdateEdit({
            text: preValue,
            words: splitWords({
              words: editedWordsRef.current,
              text: preValue,
            }),
          });

    const nextItemIsParagraphInSameTurn =
      isTurnParagraph(nextItem) && nextItem.turn.id === paragraph.turn.id;

    const createNewParagraphInSameTurn = event.shiftKey;

    // generate a new turn from the existing turn
    const newTurnOrParagraphEdit: TranscriptEdit = {
      type: "turn",
      after: createNewTurnFromParagraph(paragraph, {
        asNewParagraph: createNewParagraphInSameTurn,
        words: splitWords({
          words: editedWordsRef.current,
          text: postValue,
          direction: "reverse",
        }),
      }),
      options: nextItemIsParagraphInSameTurn
        ? { nextParagraphId: nextItem?.id }
        : undefined,
    };

    const addFn = createNewParagraphInSameTurn ? addParagraph : addTurn;

    if (createNewParagraphInSameTurn && nextItemIsParagraphInSameTurn) {
      // if the next item is a paragraph in the same turn, when we're inserting
      // a new paragraph, we simply need to update the pointer to point to this
      // new paragraph instead
      const nextParagraphEdit: TranscriptEdit = {
        type: "turn",
        before: nextItem,
        after: {
          ...nextItem,
          previousParagraphId: newTurnOrParagraphEdit.after.id,
        },
      };
      const batchEdit: TranscriptEdit = {
        edits: compact([
          updateCurrentEdit,
          newTurnOrParagraphEdit,
          nextParagraphEdit,
        ]),
      };
      return addFn(batchEdit);
    }

    // the store will handle appending edits that shift the next turn's
    // previous turn id for all paragraphs
    const edit: TranscriptEdit = updateCurrentEdit
      ? {
          edits: [updateCurrentEdit, newTurnOrParagraphEdit],
        }
      : newTurnOrParagraphEdit;

    addFn(edit);
  };

  return (
    <EditableContent
      editing={isEditingParagraph}
      onDelete={handleDeleteTurn}
      onDiscard={handleDiscard}
      onEnter={handleEnter}
      onInput={handleInput}
      onSave={handleSaveEdits}
      onToggleEditing={handleToggleEditing}
      value={paragraph.text}
      contentChunks={chunkedContent}
      type="turn"
    />
  );
};

export default EditableTranscriptTurnItemContent;
